import { fetchApiAuth } from '../utils/api';

// Timeout values in milliseconds - configurable via env
const SUMMARY_TIMEOUT = parseInt(process.env.REACT_APP_AI_SUMMARY_TIMEOUT, 10) || 30000; // 30 seconds
const QUESTION_TIMEOUT = parseInt(process.env.REACT_APP_AI_QUESTION_TIMEOUT, 10) || 30000; // 30 seconds

// Content moderation - inappropriate patterns to filter (client-side pre-check)
const INAPPROPRIATE_PATTERNS = [
    // Violence and harm
    /\b(kill|murder|hurt|harm|attack|violence|suicide|self.?harm)\b/i,
    // Hate speech indicators
    /\b(hate|racist|sexist|discriminat)\b/i,
    // Illegal activities
    /\b(illegal|drugs|steal|hack|fraud)\b/i,
    // Explicit content
    /\b(sex|porn|nude|explicit)\b/i,
    // Personal info requests
    /\b(password|credit.?card|social.?security|bank.?account)\b/i,
];

// Check if question is appropriate (client-side pre-check)
export const isQuestionAppropriate = (question) => {
    const normalizedQuestion = question.toLowerCase().trim();
    const hasInappropriateContent = INAPPROPRIATE_PATTERNS.some(pattern => pattern.test(normalizedQuestion));
    return !hasInappropriateContent;
};

// Moderation error message
export const MODERATION_ERROR = 'Your question contains content that cannot be processed. Please ask questions related to your horoscope reading.';

/**
 * Fetch AI summary from backend API
 * @param {Object} params - { content: string }
 * @returns {Promise<string>} - Summary text
 */
export const fetchAiSummary = async ({ content }) => {
    const response = await fetchApiAuth({
        method: 'POST',
        url: '/ai/summary',
        body: { content },
        timeout: SUMMARY_TIMEOUT,
    });

    const { data } = response;

    if (!data.success) {
        throw new Error(data.message || 'Failed to generate summary');
    }

    return data.summary;
};

/**
 * Fetch AI answer from backend API
 * @param {Object} params - { content: string, question: string }
 * @returns {Promise<string>} - Answer text
 */
export const fetchAiAnswer = async ({ content, question }) => {
    const response = await fetchApiAuth({
        method: 'POST',
        url: '/ai/answer',
        body: { content, question },
        timeout: QUESTION_TIMEOUT,
    });

    const { data } = response;

    if (!data.success) {
        // Handle specific error codes from backend
        if (data.error === 'INAPPROPRIATE_CONTENT') {
            throw new Error(MODERATION_ERROR);
        }
        throw new Error(data.message || 'Failed to get answer');
    }

    return data.answer;
};
